import { code as isoCurrency } from "currency-codes";
import { z } from "zod";

import { ReploError } from "./errors";

export class InvalidAmountError extends ReploError {}
export class UnsupportedCurrencyError extends ReploError {}

// NOTE (Max, 2026-08-04): minor-unit exponents come from the maintained ISO-4217
// `currency-codes` library, not a hand-kept table. ISO matches Stripe's
// convention for every currency we support (USD/EUR 2, JPY/KRW 0, KWD/BHD 3).
// It diverges only for a handful of eroded or non-decimal currencies where
// Stripe deliberately differs — e.g. ISO calls IQD/LYD 3-decimal and ISK/UGX
// 0-decimal, but Stripe treats all four as 2-decimal (and MGA/MRU are ÷5, not
// decimal at all). Because our minor units are sent to Stripe as `unit_amount`,
// we don't support those: they're rejected here so a wrong-exponent amount can
// never reach Stripe. When we actually launch one, move it to a Stripe-verified
// override with a test.
const STRIPE_UNSUPPORTED_CURRENCIES = new Set([
  "ISK",
  "UGX",
  "IQD",
  "LYD",
  "MGA",
  "MRU",
]);

function currencyMinorUnitExponent(currencyCode: string): number {
  const normalizedCode = currencyCode.toUpperCase();
  if (STRIPE_UNSUPPORTED_CURRENCIES.has(normalizedCode)) {
    throw new UnsupportedCurrencyError({
      message:
        "Currency's ISO minor-unit exponent differs from Stripe's; not supported",
      additionalData: { currencyCode: normalizedCode },
    });
  }
  return isoCurrency(normalizedCode)?.digits ?? 2;
}

/**
 * Converts a decimal major-unit amount (e.g. "19.99", 19.99) into integer
 * minor units (1999) using the currency's Stripe exponent. Half-away-from-zero
 * rounding for the non-negative amounts this domain uses (prices, totals),
 * matching Stripe. Throws on non-numeric input.
 */
export function fromMajorUnitsToMinorUnits({
  amount,
  currencyCode,
}: {
  amount: number | string;
  currencyCode: string;
}): number {
  // NOTE (Max, 2026-07-30): Number("") === 0, so blank input must be rejected
  // before coercion or a missing amount silently becomes free.
  const majorUnits =
    typeof amount === "string" && amount.trim() === ""
      ? Number.NaN
      : Number(amount);
  if (!Number.isFinite(majorUnits)) {
    throw new InvalidAmountError({
      message: "Money amount is not a finite number",
      additionalData: { amount, currencyCode },
    });
  }
  const scaled = majorUnits * 10 ** currencyMinorUnitExponent(currencyCode);
  // NOTE (Max, 2026-07-30): float multiply can land just below the true value
  // (19.995 * 100 === 1999.4999...), which would break half-up rounding;
  // re-quantizing to 15 significant digits restores the decimal the user typed.
  return Math.round(Number(scaled.toPrecision(15)));
}

/**
 * NOTE (Max, 2026-08-03): safe companion to fromMajorUnitsToMinorUnits for
 * possibly-blank or user/CSV/provider-entered decimal strings — returns null
 * for blank or non-numeric input instead of throwing. Callers with a
 * source-specific cleaning or validation policy sanitize first, then convert
 * here, so every "decimal string → minor units" path shares one converter.
 */
export function parseDecimalToMinorUnits({
  value,
  currencyCode,
}: {
  value: string | null;
  currencyCode: string;
}): number | null {
  const trimmed = value?.trim();
  if (!trimmed || !Number.isFinite(Number(trimmed))) {
    return null;
  }
  return fromMajorUnitsToMinorUnits({ amount: trimmed, currencyCode });
}

/**
 * Converts integer minor units (1999) back into a decimal major-unit number
 * (19.99). Only for true decimal boundaries: display, analytics events, and
 * third-party APIs that speak major units.
 */
export function fromMinorUnitsToMajorUnits({
  amount,
  currencyCode,
}: {
  amount: number;
  currencyCode: string;
}): number {
  return amount / 10 ** currencyMinorUnitExponent(currencyCode);
}

/**
 * Renders integer minor units as a plain decimal string for providers that
 * speak decimal money (e.g. Printful retail_price): 1999 -> "19.99" (USD),
 * "1999" (JPY), "1.999" (KWD).
 */
export function fromMinorUnitsToMajorUnitsString({
  amount,
  currencyCode,
}: {
  amount: number;
  currencyCode: string;
}): string {
  return fromMinorUnitsToMajorUnits({ amount, currencyCode }).toFixed(
    currencyMinorUnitExponent(currencyCode),
  );
}

/**
 * Formats integer minor units for display, e.g. 1999 -> "$19.99" (USD),
 * "¥1,999" (JPY).
 */
export function formatAmount({
  amount,
  currencyCode,
  locale,
}: {
  amount: number;
  currencyCode: string;
  locale?: string;
}): string {
  return new Intl.NumberFormat(locale ?? "en-US", {
    style: "currency",
    currency: currencyCode.toUpperCase(),
  }).format(fromMinorUnitsToMajorUnits({ amount, currencyCode }));
}

// NOTE (Max, 2026-08-04): naming convention for the currency of a money value.
// Canonical / internal commerce money always pairs an `amount` with a
// `currencyCode` (the ISO 4217 code, e.g. "USD"). The bare word `currency` is
// reserved for the edges where we mirror a third party's wire field — Stripe
// and Printful both call it `currency` — and it is converted to `currencyCode`
// at that boundary (e.g. the provider→canonical product mapper), exactly like
// we lowercase only at the Stripe edge. So `currency` vs `currencyCode` is a
// boundary distinction, not interchangeable synonyms: internal = currencyCode.
export const amountSchema = z
  .number()
  .int()
  .describe(
    "Integer amount in the currency's minor units (Stripe convention): 1999 = $19.99 USD, 1999 = ¥1999 JPY, 1999 = 1.999 KWD",
  );

/** An amount in minor units paired with its ISO 4217 currency code. */
export const amountWithCurrencyCodeSchema = z.object({
  amount: amountSchema,
  currencyCode: z.string(),
});
